import { getRoutes } from '@/helpers/helpers'
import { LinkDto, ShareLink } from '@/models/AdditionalDataTypes'
import { FilterPageDto, FilterPagesDto, StoryDto, StorySearchModel, FilterPageTag, StorytellerTag, PlaylistDto, PlaylistsGroupDto, TourGroupDto, HybridMapDto } from '@/models/FilterPageTypes'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useFilterPageStore = defineStore('filterPageStore', () => {
    const filterPageInitDtoUrl = '/api/FilterPage'
    const filteredStoriesUrl = 'api/FilterPage/FilterStories'
    const filteredToursUrl = 'api/FilterPage/GetMoreTours'
    const filteredStoryByKeyUrl = 'api/FilterPage/GetStory'
    const getStoriesByTourIdUrl = 'api/FilterPage/GetStoriesByTourId'

    const timeTagSearchMode = '2'
    const storyPlugEvery = 10

    const storiesPageTitle = ref<string>();
    const stories = ref<Array<StoryDto>>([])
    const storiesShareLinks = ref<ShareLink[]>([])
    const locationTags = ref<FilterPageTag[]>([])
    const timeTags = ref<FilterPageTag[]>([])
    const contentTags = ref<FilterPageTag[]>([])
    const storytellerTags = ref<StorytellerTag[]>([])
    const storiesDescr = ref<string>()
    const storiesPageSize = ref<number>(0)
    const totalStories = ref<number>(0)
    const isStoriesFetching = ref<boolean>(false)
    const pageSize = ref<number>(0)

    const googleMapKey = ref<string>('')

    const playlistPageTitle = ref<string>();
    const playlistsGroups = ref<Array<PlaylistsGroupDto>>([])
    const playlistsOverviewDescr = ref<string>()

    const toursPageTitle = ref<string>()
    const toursGroups = ref<Array<TourGroupDto>>([])
    const toursDescr = ref<string>()
    const isToursFetching = ref<boolean>(false)
    const toursPageSize = ref<number>(0)
    const totalTours = ref<number>(0)

    const landingPageLink = ref<string>('')
    const createNewStory = ref<LinkDto>({})

    const locallizations = ref<{ [key: string]: string }>({})

    const storySearchModel = ref<StorySearchModel>({
        timeTags: [],
        storytellerTags: [],
        yearsRange: [1930, new Date().getFullYear() + 1],
        locationTags: [],
        locationQuery: '',
        contentTags: [],
        locationType: '1',
        timeType: '1',
        query: '',
    })

    const storiesLength = computed(() => {
        const createStoryText = getLocalization('FilterPage.StoryCollection.CreateStoryItemText') ?? ''
        const isFakeStoryExists = stories.value.find((i) => i.Description.includes(createStoryText)) ?? null

        return isFakeStoryExists ? stories.value.length - 1 : stories.value.length
    })

    const sliceArray = (stories: Array<StoryDto>) => {
        const chunkSize = storyPlugEvery
        const chunks = [] as Array<StoryDto[]>

        for (let i = 0; i < stories.length; i += chunkSize) {
            const chunk = stories.slice(i, i + chunkSize)
            chunks.push(chunk)
        }

        return chunks
    }

    function shuffleStoryCreationInto(stories: Array<StoryDto>): Array<StoryDto> {
        return stories
    }

    function returnPlaylists(): Array<PlaylistDto> {
        let playlists = new Array<PlaylistDto>()
        playlistsGroups.value.forEach((element) => {
            playlists = playlists.concat(element.Playlists)
        })

        return playlists
    }

    async function getStoriesByTourId(tourId:number) {
        const currentCulture = 
            document.documentElement.dataset.culture?.toString() ?? ''
        const url =
            getStoriesByTourIdUrl +
            '?' +
            new URLSearchParams({
                tourId: tourId.toString(),
                culture: currentCulture
            })

        const resp = await fetch(url, {
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
                culture: currentCulture,
            },
        })

        const data = await resp.json() as HybridMapDto
        googleMapKey.value = data.GoogleMapKey
        
        return data
    }

    async function fetchFilterPage() {
        isStoriesFetching.value = true
        const currentCulture =
            document.documentElement.dataset.culture?.toString() ?? ''
        const url =
            filterPageInitDtoUrl +
            "?" +
            new URLSearchParams({
                culture: currentCulture,
            });
        const resp = await fetch(url, {
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
                culture: currentCulture,
            },
        })
        const data = (await resp.json()) as FilterPagesDto
        
        storiesPageTitle.value = data.StoriesPage.PageTitle
        storiesDescr.value = data.StoriesPage.Description
        storiesShareLinks.value = data.StoriesPage.ShareLinks
        storiesPageSize.value = data.StoriesPage.PageSize
        pageSize.value = data.StoriesPage.PageSize

        playlistPageTitle.value = data.PlaylistsPage.PageTitle
        playlistsGroups.value = data.PlaylistsPage.Items
        playlistsOverviewDescr.value = data.PlaylistsPage.Description

        toursPageTitle.value = data.ToursPage.PageTitle
        toursGroups.value = data.ToursPage.Items
        toursDescr.value = data.ToursPage.Description
        toursPageSize.value = data.ToursPage.PageSize
        totalTours.value = data.ToursPage.TotalItems

        locallizations.value = data.Translations
        landingPageLink.value = data.LandingPageLink
        createNewStory.value = data.CreateNewStory
        locationTags.value = data.StoriesPage.Tags.LocationTags
        timeTags.value = data.StoriesPage.Tags.TimeTags
        contentTags.value = data.StoriesPage.Tags.ContentTags
        storytellerTags.value = data.StoriesPage.Tags.StorytellerTags

        googleMapKey.value = data.GoogleMapKey

        // get stories independently, in case of filters
        await searchStories()
        isStoriesFetching.value = false
    }

    async function getMoreStories() {
        if (stories.value.length >= totalStories.value) {
            return
        }

        const data = await fetchStories()
        
        const createStoryText = getLocalization('FilterPage.StoryCollection.CreateStoryItemText') ?? ''
        const chunks = sliceArray(data.Items)

        if (createStoryText.length > 0) {
            const storyCreation = <StoryDto>{
                PreviewType: 5,
                Description: `<p style="text-align: center;padding: 55px 0;">${createStoryText}</p>`,
                BackgroundColor: '#1c613b', //TODO: what does mean "The background color will change according to the museum's color palette." ?
            }

            chunks.forEach((ch) => {
                const plugPos = Math.random() * (ch.length - 0) + 0
                ch.splice(plugPos, 0, storyCreation)
            })
        }

        stories.value.push(...chunks.flatMap((i) => i))

        totalStories.value = data.TotalItems
    }

    async function searchStoryByTag(tag: string) {
        let searchKey = timeTags.value.find((element) => element.Name == tag)?.Key
        if (searchKey) {
            storySearchModel.value.timeType = timeTagSearchMode
            storySearchModel.value.timeTags = [searchKey]
            return
        }

        searchKey = contentTags.value.find((element) => element.Name == tag)?.Key
        if (searchKey) {
            storySearchModel.value.contentTags = [searchKey]
            return
        }

        searchKey = locationTags.value.find((element) => element.Name == tag)?.Key
        if (searchKey) {
            storySearchModel.value.locationTags = [searchKey]
            return
        }
    }

    function restFilters() {
        storySearchModel.value.storytellerTags = []
        storySearchModel.value.locationTags = []
        storySearchModel.value.contentTags = []
        storySearchModel.value.timeTags = []
        storySearchModel.value.yearsRange = []
        storySearchModel.value.timeType = '1'
        storySearchModel.value.locationType = '1'
        storySearchModel.value.locationQuery = ''
    }

    function isAnyFiltersPushed() {
        return storySearchModel.value.storytellerTags.length ||
            storySearchModel.value.locationTags.length ||
            storySearchModel.value.contentTags.length ||
            storySearchModel.value.timeTags.length ||
            storySearchModel.value.yearsRange.length ||
            storySearchModel.value.locationQuery.length 
    }

    async function searchStories() {
        const data = await fetchStories()

        stories.value.push(...data.Items)
        totalStories.value = data.TotalItems
    }

    async function fetchStoryByKey(keyId: string) {
        const currentCulture =
            document.documentElement.dataset.culture?.toString() ?? ''
        const url =
            filteredStoryByKeyUrl +
            '?' +
            new URLSearchParams({
                keyId,
                culture: currentCulture
            })

        const resp = await fetch(url, {
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
                culture: currentCulture,
            },
        })

        if (resp.ok) {
            return (await resp.json()) as StoryDto
        }
        if (resp.status === 302) {
            const redirect = await resp.text()
            throw new Error(redirect);
        }
        
        return undefined
    }

    async function fetchStories() {
        isStoriesFetching.value = true
        const model = {
            ...storySearchModel.value,
            from: storiesLength.value,
            pageSize: pageSize.value,
        }

        const currentCulture =
            document.documentElement.dataset.culture?.toString() ?? ''
        const url =
            filteredStoriesUrl +
            "?" +
            new URLSearchParams({
                culture: currentCulture,
            });

        const resp = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json;charset=utf-8",
                culture: currentCulture,
            },
            body: JSON.stringify(model),
        });

        const stories = (await resp.json()) as FilterPageDto<StoryDto>
        isStoriesFetching.value = false

        return stories
    }

    async function fetchTours(query: string, category: string, from: number, pageSize: number) {
         const currentCulture =
             document.documentElement.dataset.culture?.toString() ?? ''
        const url =
            filteredToursUrl +
            "?" +
            new URLSearchParams({
                query: query,
                category: category,
                from: from + "",
                pageSize: pageSize + "",
                culture: currentCulture,
            });

        const resp = await fetch(url, {
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
                culture: currentCulture,
            },
        })

        if (resp.ok) {
            return (await resp.json()) as FilterPageDto<TourGroupDto>
        }

        return undefined
    }
    async function searchTours(query: string, category: string, from: number, pageSize: number) {
        isToursFetching.value = true

        const data = await fetchTours(query, category, from, pageSize)

        if (data) {
            toursGroups.value = [...data.Items]
            totalTours.value = data.TotalItems
        } else {
            toursGroups.value = []
            totalTours.value = 0
        }

        isToursFetching.value = false
    }

    async function getMoreTours(query: string, category: string, from: number, pageSize: number) {
        isToursFetching.value = true

        const data = await fetchTours(query, category, from, pageSize)

        if (data) {
            const arr = [...toursGroups.value]
            data?.Items.forEach((i) => {
                const listToAppend = arr.find((g) => g.GroupName === i.GroupName)
                listToAppend?.Items.push(...i.Items)
            })

            toursGroups.value = [...arr]
        }

        isToursFetching.value = false
    }

    function getLocalization(key: string) {
        let res = ''

        try {
            res = locallizations.value[key]
        } catch (error) {
            console.warn(`No localization for ${key}`)
        }

        return res
    }

    const { storiesRoute, playlistsRoute, toursRoute } = getRoutes("vue-filter-page-component");

    function isToursPage(routePath: string) {
        return routePath.includes(toursRoute);
    }
    function isPlaylistsPage(routePath: string) {
        return routePath.includes(playlistsRoute);
    }
    function isStoriesPage(routePath: string) {
        return routePath.includes(storiesRoute);
    }

    return {
        storiesPageTitle,
        playlistPageTitle,
        toursPageTitle,
        stories,
        toursGroups,
        storiesDescr,
        storiesShareLinks,
        toursDescr,
        playlistsGroups,
        playlistsOverviewDescr,
        storiesPageSize,
        isStoriesFetching,
        isToursFetching,
        totalStories,
        totalTours,
        landingPageLink,
        createNewStory,
        toursPageSize,
        googleMapKey,
        fetchFilterPage,
        getMoreStories,
        getStoriesByTourId,
        searchStoryByTag,
        getLocalization,
        shuffleStoryCreationInto,
        returnPlaylists,
        restFilters,
        isAnyFiltersPushed,
        timeTags,
        locationTags,
        contentTags,
        storytellerTags,
        storySearchModel,
        searchStories,
        searchTours,
        getMoreTours,
        fetchStoryByKey,
        isToursPage,
        isPlaylistsPage,
        isStoriesPage,
    }
})
